<template>
  <div class="settings">
    <div class="panel settings__navigation">
      <app-sidebar
        :title="$t('setting_title')"
        :items="navigationItems"
      ></app-sidebar>
    </div>

    <div class="panel settings__content">
      <attribute-templates
        v-if="isAttributeTemplates"
        :items="attributeTemplates"
        :languages="languages"
        :references="references"
        :attributeTypes="attributeTypes"
        :loading="loading"
        @create="create"
        @update="update"
        @delete="remove"
      ></attribute-templates>

      <object-types
        v-else-if="isObjectType"
        :items="objectTypes"
        :systems="systems"
        :loading="loading"
        @create="create"
        @update="update"
      ></object-types>

      <object-templates
        v-else-if="isObjectTemplates"
        :items="objectTemplates"
        :objectTypes="objectTypes"
        :attributeTemplates="attributeTemplates"
        :languages="languages"
        :attributeTypes="attributeTypes"
        :references="references"
        :systems="systems"
        :loading="loading"
        @read="read"
        @create="create"
        @update="update"
        @delete="remove"
      ></object-templates>
    </div>
  </div>
</template>

<script>
import AppSidebar from "@/components/library.blocks/AppSidebar.vue";

import { mapState } from "vuex";
import { Response, System } from "@/mixins";

import {
  ATTRIBUTE_TEMPLATES_GET,
  ATTRIBUTE_TEMPLATES_CREATE,
  ATTRIBUTE_TEMPLATES_UPDATE,
  ATTRIBUTE_TEMPLATES_DELETE,
  OBJECT_TYPES_GET,
  OBJECT_TYPES_CREATE,
  OBJECT_TYPES_UPDATE,
  OBJECT_TEMPLATES_READ,
  OBJECT_TEMPLATES_CLEAR,
  OBJECT_TEMPLATES_CREATE,
  OBJECT_TEMPLATES_UPDATE,
  OBJECT_TEMPLATES_DELETE,
  REFERENCE_REFS,
  SYSTEM_REFS,
  LANGUAGE_REFS,
  ATTRIBUTE_TYPE_REFS
} from "@/store/type";

import { CREATE, READ, UPDATE, DELETE } from "@/domain/crud.types";

export default {
  name: "settings",
  components: {
    AppSidebar,
    AttributeTemplates: () =>
      import(
        /* webpackChunkName: "attributeTemplates" */ "@/components/common.blocks/settings/AttributeTemplates.vue"
      ),
    ObjectTypes: () =>
      import(
        /* webpackChunkName: "objectTypes" */ "@/components/common.blocks/settings/ObjectTypes.vue"
      ),
    ObjectTemplates: () =>
      import(
        /* webpackChunkName: "objectTemplates" */ "@/components/common.blocks/settings/ObjectTemplates.vue"
      )
  },
  mixins: [Response, System],
  data() {
    return {
      OBJECT_TEMPLATE: "object_template",
      OBJECT_TYPE: "object_type",
      ATTRIBUTE_TEMPLATES: "attribute_templates",
      setting: undefined,
      loading: false
    };
  },
  computed: {
    ...mapState({
      attributeTemplates: state => state.attributeTemplates.attributeTemplates,
      objectTypes: state => state.objectTypes.objectTypes,
      objectTemplates: state => state.objectTemplates.objectTemplates,
      lang: state => state.settings.lang,
      systems: state => state.references.systems,
      references: state => state.references.references,
      languages: state => state.references.languages,
      attributeTypes: state => state.references.attributeTypes
    }),
    settingList() {
      let result = {};
      result[this.ATTRIBUTE_TEMPLATES] = [
        { mutation: ATTRIBUTE_TEMPLATES_GET }
      ];

      result[this.OBJECT_TYPE] = [{ mutation: OBJECT_TYPES_GET }];

      return result;
    },
    referenceLoading() {
      let result = {};
      result[this.ATTRIBUTE_TEMPLATES] = [
        { mutation: ATTRIBUTE_TEMPLATES_GET, refs: "attributeTemplates" },
        { mutation: REFERENCE_REFS, refs: "references" },
        { mutation: LANGUAGE_REFS, refs: "languages" },
        { mutation: ATTRIBUTE_TYPE_REFS, refs: "attributeTypes" }
      ];
      result[this.OBJECT_TYPE] = [
        { mutation: OBJECT_TYPES_GET, refs: "objectTypes" },
        { mutation: SYSTEM_REFS, refs: "systems" }
      ];
      result[this.OBJECT_TEMPLATE] = [
        { mutation: OBJECT_TYPES_GET, refs: "objectTypes" },
        { mutation: ATTRIBUTE_TEMPLATES_GET, refs: "attributeTemplates" },
        { mutation: REFERENCE_REFS, refs: "references" },
        { mutation: SYSTEM_REFS, refs: "systems" },
        { mutation: LANGUAGE_REFS, refs: "languages" },
        { mutation: ATTRIBUTE_TYPE_REFS, refs: "attributeTypes" }
      ];
      return result;
    },
    actionsCRUD() {
      let result = {};
      result[this.ATTRIBUTE_TEMPLATES] = [
        { action: CREATE, mutation: ATTRIBUTE_TEMPLATES_CREATE },
        { action: UPDATE, mutation: ATTRIBUTE_TEMPLATES_UPDATE },
        { action: DELETE, mutation: ATTRIBUTE_TEMPLATES_DELETE }
      ];
      result[this.OBJECT_TYPE] = [
        { action: CREATE, mutation: OBJECT_TYPES_CREATE },
        { action: UPDATE, mutation: OBJECT_TYPES_UPDATE }
      ];
      result[this.OBJECT_TEMPLATE] = [
        { action: READ, mutation: OBJECT_TEMPLATES_READ },
        { action: CREATE, mutation: OBJECT_TEMPLATES_CREATE },
        { action: UPDATE, mutation: OBJECT_TEMPLATES_UPDATE },
        { action: DELETE, mutation: OBJECT_TEMPLATES_DELETE }
      ];
      return result;
    },
    navigationItems() {
      return [
        {
          id: this.OBJECT_TEMPLATE,
          name: this.$t("settings_nav_template_obj"),
          url: `${this.$route.path}?type=${this.OBJECT_TEMPLATE}`,
          selected: this.isObjectTemplates
        },
        {
          id: this.OBJECT_TYPE,
          name: this.$t("settings_nav_obj_type"),
          url: `${this.$route.path}?type=${this.OBJECT_TYPE}`,
          selected: this.isObjectType
        },
        {
          id: this.ATTRIBUTE_TEMPLATES,
          name: this.$t("settings_nav_template_attr"),
          url: `${this.$route.path}?type=${this.ATTRIBUTE_TEMPLATES}`,
          selected: this.isAttributeTemplates
        }
      ];
    },
    isAttributeTemplates() {
      return this.ATTRIBUTE_TEMPLATES == this.setting;
    },
    isObjectType() {
      return this.OBJECT_TYPE == this.setting;
    },
    isObjectTemplates() {
      return this.OBJECT_TEMPLATE == this.setting;
    }
  },
  methods: {
    getUrlSettings() {
      let q = this.$route.query;
      if (q.type) {
        let nav = this.navigationItems.filter(
          item => q.type.toLowerCase() == item.id.toLowerCase()
        );

        if (nav.length == 1) {
          this.setting = nav[0].id;
          this.replaceUrl();
        } else {
          this.setDefaultUrl();
        }
      } else {
        this.setDefaultUrl();
      }
    },
    setDefaultUrl() {
      this.setting = this.navigationItems.length
        ? this.navigationItems[0].id
        : this.ATTRIBUTE_TEMPLATES;

      this.replaceUrl();
    },
    replaceUrl() {
      let q = this.$route.query;
      if (q.type && q.type == this.setting) {
        return false;
      }

      this.$router.replace({
        query: { type: this.setting }
      });
    },
    resetObjectList() {
      this.$store.commit(OBJECT_TEMPLATES_CLEAR);
    },
    getSettingList() {
      if (this.settingList[this.setting]) {
        this.settingList[this.setting].forEach(item => {
          if (item.mutation) {
            this.$store.dispatch(item.mutation, this.lang);
          } else if (item.callback) {
            item.callback();
          }
        });
      }
    },
    getReferenceLoading() {
      if (this.referenceLoading[this.setting]) {
        this.referenceLoading[this.setting].forEach(item => {
          if (this[item.refs].length === 0) {
            this.$store.dispatch(item.mutation, this.lang);
          }
        });
      }
    },
    getData() {
      this.getSettingList();
      this.getReferenceLoading();
    },
    dispatchCRUD(mutation, params) {
      this.loading = true;
      this.$store
        .dispatch(mutation, params)
        .then(msg => {
          this.parseResponse(msg); // mixins
          this.getSettingList();
          this.loading = false;
        })
        .catch(errStatus => {
          this.parseResponseError(errStatus); // mixins
          this.loading = false;
        });
    },
    executeAction(action, params) {
      if (this.actionsCRUD[this.setting]) {
        this.actionsCRUD[this.setting]
          .filter(item => item.action === action)
          .forEach(item => {
            this.dispatchCRUD(item.mutation, params);
          });
      }
    },
    injectLangParams(params) {
      return { ...params, lang: this.lang };
    },
    update(params) {
      this.executeAction(UPDATE, this.injectLangParams(params));
    },
    create(params) {
      this.executeAction(CREATE, this.injectLangParams(params));
    },
    remove(params) {
      this.executeAction(DELETE, params);
    },
    read(params) {
      this.executeAction(READ, this.injectLangParams(params));
    }
  },
  mounted() {
    this.getUrlSettings();
    this.getData();
  },
  watch: {
    "$route.query"() {
      this.resetObjectList();
      this.getUrlSettings();
      this.getData();
    }
  }
};
</script>

<style lang="scss">
@import "@/css/variables.scss";

.settings {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  height: 100%;

  .settings__navigation {
    width: 20%;
    height: 100%;
    box-sizing: border-box;
  }

  .settings__content {
    margin-left: 10px;
    padding: 5px 10px;
    width: 80%;
    height: 100%;
    overflow: auto;
    box-sizing: border-box;
  }
}
</style>
